import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, Badge, Spinner } from "../components";
import { QueryError } from "../components/QueryError";
import { apiGet, apiPost, apiPatch } from "../lib/api";
import { useToast } from "../contexts/ToastContext";
import { useState } from "react";
// Bucket C Round 2 — single source of truth for the versions wire shape.
// Type-only import: Adam's Vite bundler never resolves the `.js` at runtime.
import type { VersionEntry } from "../../../../src/shared/contracts/versions.js";

interface VersionDistribution {
  imageTag: string;
  tenantCount: number;
}

export default function Versions() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [showRegister, setShowRegister] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [newSha, setNewSha] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [newStable, setNewStable] = useState(false);

  const {
    data: versionsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["versions"],
    queryFn: () => apiGet<{ versions: VersionEntry[]; total: number }>("/api/versions"),
  });

  const { data: distData } = useQuery({
    queryKey: ["fleet-versions"],
    queryFn: () => apiGet<{ distribution: VersionDistribution[] }>("/api/fleet/versions"),
  });

  const registerVersion = useMutation({
    mutationFn: (body: {
      imageTag: string;
      commitSha?: string;
      releaseNotes?: string;
      isStable: boolean;
    }) => apiPost("/api/versions", body),
    onSuccess: () => {
      toast.success("Version registered");
      queryClient.invalidateQueries({ queryKey: ["versions"] });
      setShowRegister(false);
      setNewTag("");
      setNewSha("");
      setNewNotes("");
      setNewStable(false);
    },
    onError: (err: Error) => toast.error("Failed to register version", err.message),
  });

  const toggleStable = useMutation({
    mutationFn: ({ id, isStable }: { id: string; isStable: boolean }) =>
      apiPatch(`/api/versions/${id}`, { isStable }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["versions"] });
    },
    onError: (err: Error) => toast.error("Failed to update version", err.message),
  });

  const promoteProduction = useMutation({
    mutationFn: (id: string) => apiPost(`/api/versions/${id}/promote-production`),
    onSuccess: () => {
      toast.success("Version promoted to production");
      queryClient.invalidateQueries({ queryKey: ["versions"] });
      queryClient.invalidateQueries({ queryKey: ["autopilot", "queue"] });
    },
    onError: (err: Error) => toast.error("Production promote failed", err.message),
  });

  const versions = versionsData?.versions ?? [];
  const distribution = distData?.distribution ?? [];
  const totalDeployed = distribution.reduce((sum, d) => sum + d.tenantCount, 0);

  if (error && !versionsData) {
    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Versions</h1>
            <p style={{ margin: "4px 0 0", color: "var(--c-text-secondary)", fontSize: 14 }}>
              Manage available deployment versions and fleet distribution
            </p>
          </div>
        </div>
        <QueryError error={error} queryKeys={[["versions"]]} />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Versions</h1>
          <p style={{ margin: "4px 0 0", color: "var(--c-text-secondary)", fontSize: 14 }}>
            Manage available deployment versions and fleet distribution
          </p>
        </div>
        <button
          onClick={() => setShowRegister(!showRegister)}
          style={{
            padding: "8px 16px",
            backgroundColor: "var(--c-primary)",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Register Version
        </button>
      </div>

      {/* Register form */}
      {showRegister && (
        <Card style={{ marginBottom: 20, padding: 16 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600 }}>
            Register New Version
          </h3>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}
          >
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                Image Tag *
              </label>
              <input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="e.g., v1.2.3 or sha-abc123"
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid var(--c-border)",
                  borderRadius: 4,
                  fontSize: 13,
                }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                Commit SHA
              </label>
              <input
                value={newSha}
                onChange={(e) => setNewSha(e.target.value)}
                placeholder="Optional"
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid var(--c-border)",
                  borderRadius: 4,
                  fontSize: 13,
                }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
              Release Notes
            </label>
            <textarea
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              rows={3}
              placeholder="What changed in this version?"
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid var(--c-border)",
                borderRadius: 4,
                fontSize: 13,
                resize: "vertical",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={newStable}
                onChange={(e) => setNewStable(e.target.checked)}
              />
              Mark as stable
            </label>
            <button
              onClick={() =>
                registerVersion.mutate({
                  imageTag: newTag,
                  commitSha: newSha || undefined,
                  releaseNotes: newNotes || undefined,
                  isStable: newStable,
                })
              }
              disabled={!newTag || registerVersion.isPending}
              style={{
                padding: "6px 16px",
                backgroundColor: "var(--c-primary)",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                fontSize: 13,
                cursor: !newTag ? "not-allowed" : "pointer",
                opacity: !newTag ? 0.6 : 1,
              }}
            >
              Register
            </button>
            <button
              onClick={() => setShowRegister(false)}
              style={{
                padding: "6px 16px",
                background: "none",
                border: "1px solid var(--c-border)",
                borderRadius: 4,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </Card>
      )}

      {/* Distribution summary */}
      {distribution.length > 0 && (
        <Card style={{ marginBottom: 20, padding: 16 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 600 }}>Fleet Distribution</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {distribution.map((d) => (
              <div
                key={d.imageTag}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "1px solid var(--c-border)",
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <code style={{ fontSize: 12 }}>{d.imageTag}</code>
                <Badge variant="neutral">
                  {d.tenantCount} tenant{d.tenantCount !== 1 ? "s" : ""}
                </Badge>
                <span style={{ fontSize: 11, color: "var(--c-text-secondary)" }}>
                  ({totalDeployed > 0 ? Math.round((d.tenantCount / totalDeployed) * 100) : 0}%)
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Version list */}
      <Card>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: "center" }}>
            <Spinner size="md" />
          </div>
        ) : versions.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--c-text-secondary)" }}>
            No versions registered yet. Register your first version to enable fleet version
            management.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.1))" }}>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Image Tag
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Commit
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Candidate
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Gate
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Blocking
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Stable
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Release Notes
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Date
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontWeight: 600,
                    color: "var(--c-text-secondary)",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr
                  key={v.id}
                  style={{ borderBottom: "1px solid var(--c-border, rgba(0,0,0,0.05))" }}
                >
                  <td style={{ padding: "10px 12px" }}>
                    <code style={{ fontSize: 12 }}>{v.imageTag}</code>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {v.commitSha ? (
                      <a
                        href={`https://github.com/agentme/openclaw/commit/${v.commitSha}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={v.commitSha}
                        style={{ textDecoration: "none" }}
                      >
                        <code
                          style={{ fontSize: 11, color: "var(--c-primary)", cursor: "pointer" }}
                        >
                          {v.commitSha.slice(0, 7)}
                        </code>
                      </a>
                    ) : (
                      <span style={{ color: "var(--c-text-faint)" }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <Badge
                      color={
                        v.candidateState === "ready_for_prod"
                          ? "green"
                          : v.candidateState === "blocked"
                            ? "red"
                            : v.candidateState === "promoted"
                              ? "blue"
                              : "gray"
                      }
                    >
                      {v.candidateState.replaceAll("_", " ")}
                    </Badge>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <Badge
                      color={
                        v.gateStatus === "staging_verified"
                          ? "blue"
                          : v.gateStatus === "promoted"
                            ? "green"
                            : v.gateStatus === "candidate"
                              ? "gray"
                              : "amber"
                      }
                    >
                      {v.gateStatus.replaceAll("_", " ")}
                    </Badge>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {v.blockingFindingsCount > 0 ? (
                      <Badge color="red">{v.blockingFindingsCount}</Badge>
                    ) : (
                      <Badge color="green">0</Badge>
                    )}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <Badge variant={v.isStable ? "green" : "neutral"}>
                      {v.isStable ? "stable" : "unstable"}
                    </Badge>
                  </td>
                  <td style={{ padding: "10px 12px", maxWidth: 300 }}>
                    {v.releaseNotes ? (
                      <span style={{ fontSize: 12, color: "var(--c-text-secondary)" }}>
                        {v.releaseNotes.length > 80
                          ? v.releaseNotes.slice(0, 80) + "..."
                          : v.releaseNotes}
                      </span>
                    ) : (
                      <span style={{ color: "var(--c-text-faint)" }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {new Date(v.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      {v.candidateState === "ready_for_prod" ? (
                        <button
                          onClick={() => promoteProduction.mutate(v.id)}
                          disabled={promoteProduction.isPending}
                          style={{
                            padding: "4px 10px",
                            fontSize: 12,
                            border: "none",
                            borderRadius: 4,
                            background: "var(--c-primary)",
                            color: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          Promote to Prod
                        </button>
                      ) : null}
                      <button
                        onClick={() => toggleStable.mutate({ id: v.id, isStable: !v.isStable })}
                        style={{
                          padding: "4px 10px",
                          fontSize: 12,
                          border: "1px solid var(--c-border)",
                          borderRadius: 4,
                          background: "none",
                          cursor: "pointer",
                          color: "var(--c-text-secondary)",
                        }}
                      >
                        {v.isStable ? "Mark Unstable" : "Mark Stable"}
                      </button>
                    </div>
                    {v.lastStagingVerificationAt ? (
                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 11,
                          color: "var(--c-text-faint)",
                        }}
                      >
                        Staging verified {new Date(v.lastStagingVerificationAt).toLocaleString()}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
